import React, { useCallback, useState } from "react";
import { Spinner } from "@fluentui/react-components";
import { TaskListLtr20Regular } from "@fluentui/react-icons";
import { useNavigate } from "react-router";
import type { CallLogDto } from "../../services/telephonyService";
import { loese, serverMeldung } from "./callVorgangZuweisen";
import { zuordnungLabel, zuordnungenVon } from "./callZuordnungen";

/**
 * Die Vorgänge eines Anrufs im Anruf-Detail (PO 01.10.2026: „Ein Anruf kann
 * man mehreren Vorgängen (und mehreren To-dos innerhalb eines Vorgangs)
 * zuweisen.").
 *
 * Ein Chip je Zuordnung „4711-2 · Titel › To-do": Klick öffnet den Vorgang,
 * × löst genau diese Zuordnung (andere bleiben). Zuordnungen an Vorgängen,
 * die der Benutzer nicht lesen darf, erscheinen nur als Zahl. Hinzufügen geht
 * über den Knopf „Vorgang ▾" in der Aktionsleiste. Muster: `CallKontakteBlock`.
 */
const CallVorgaengeBlock: React.FC<{
    call: CallLogDto;
    onChanged: (next: CallLogDto) => void;
}> = ({ call, onChanged }) => {
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const zuordnungen = zuordnungenVon(call);
    const verborgen = call.weitereZuordnungenVerborgen ?? 0;

    const loesen = useCallback(
        async (zuordnungId: number) => {
            setBusy(true);
            setError(null);
            try {
                onChanged(await loese(call, zuordnungId));
            } catch (e) {
                setError(serverMeldung(e) ?? "Die Zuordnung konnte nicht gelöst werden.");
            } finally {
                setBusy(false);
            }
        },
        [call, onChanged],
    );

    if (zuordnungen.length === 0 && verborgen === 0) return null;

    return (
        <div className="tp-block">
            <div className="tp-block-h">
                <TaskListLtr20Regular />
                {zuordnungen.length + verborgen === 1 ? "Vorgang" : "Vorgänge"}
                {busy && <Spinner size="tiny" />}
                {error && (
                    <span className="tp-muted" style={{ color: "var(--danger)" }}>
                        {error}
                    </span>
                )}
            </div>
            <div className="tp-kchips">
                {zuordnungen.map((z) => {
                    const label = zuordnungLabel(z);
                    return (
                        <span key={z.id ?? `s${z.vorgangId}`} className={`tp-kchip${z.istPrimaer ? " primary" : ""}`}>
                            <button
                                type="button"
                                className="tp-kchip-name"
                                onClick={() => navigate(`/vorgaenge/${z.vorgangId}`)}
                                title={`Vorgang öffnen: ${label}`}
                            >
                                {label}
                            </button>
                            {z.id != null && (
                                <button
                                    type="button"
                                    className="tp-kchip-act"
                                    disabled={busy}
                                    onClick={() => void loesen(z.id as number)}
                                    aria-label={`Anruf von ${label} lösen`}
                                    title="Aus diesem Vorgang bzw. To-do lösen — andere Zuordnungen bleiben"
                                >
                                    ×
                                </button>
                            )}
                        </span>
                    );
                })}
                {verborgen > 0 && (
                    <span className="tp-muted">
                        + {verborgen} weitere (keine Berechtigung)
                    </span>
                )}
            </div>
        </div>
    );
};

export default CallVorgaengeBlock;
